import React, { createContext, useContext } from 'react';

/**
 * The authenticated agent's display name, sourced from the `name` OIDC claim
 * and surfaced by the BFF on `/api/session` (#608 CCF-105). Provided once at
 * the app root and consumed by screens that need to render the agent's name —
 * currently the Outbound Security screen's regulatory scripting greeting.
 *
 * `undefined` ⇒ the IdP did not issue a `name` claim for this session (e.g.
 * Cognito sandbox), in which case the screen falls back to a neutral placeholder
 * cue. This keeps the existing behaviour for sessions without a name and for any
 * screen rendered outside the provider (e.g. unit tests).
 */
const AgentNameContext = createContext<string | undefined>(undefined);

export function AgentNameProvider({
  agentName,
  children,
}: {
  agentName?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <AgentNameContext.Provider value={agentName}>
      {children}
    </AgentNameContext.Provider>
  );
}

/** The agent's display name from the session, or undefined when unavailable. */
export function useAgentName(): string | undefined {
  return useContext(AgentNameContext);
}
